import React, {
  ReactNode, useCallback, useId, useState,
} from 'react';

import Button from 'components/DesignSystem/Button';
import { useMyStudiesProduct } from 'components/Flux/Personalized/MyStudies/product';
import StudyStatusPill from 'components/Flux/Personalized/MyStudies/StudyStatusPill';
import {
  formatExecutionLine,
  formatHorizon,
  formatResponseLine,
  isCreatedByMe,
} from 'helper/flux/myStudyRow';
import { formatInstantDate } from 'helper/flux/studyDates';
import { MyStudy, StudyOrigin } from 'types/Flux/Personalized/myStudies';

import './index.scss';

const EXPAND_TITLE = 'Ver cenários e blocos';
const COLLAPSE_TITLE = 'Recolher';
const INTERRUPTED_MESSAGE = 'Processamento interrompido. Verifique seu e-mail.';
/** Canal do envio (#80): o backend decide pelo User-Agent; `null` ou ausente = sem texto. */
const ORIGIN_LABEL: Record<StudyOrigin, string> = { api: 'via API', tela: 'via tela' };
const ORIGIN_TITLE: Record<StudyOrigin, string> = {
  api: 'Enviado pela API (script ou pacote Python)',
  tela: 'Enviado pelo site',
};
/** Id do estudo no banco: é o que o suporte pede para localizar o estudo (log, API, download). */
const ID_TITLE = 'ID do estudo — informe ao suporte ao relatar um problema';

export interface StudyRowProps {
  study: MyStudy;
  /** Id do usuário logado: marca "(você)" só quando ele é o criador (admin não ganha a marca). */
  userId: number;
  /** Agora, em ms desde a época, lido pelo pai a cada render: "hoje" não envelhece na tela. */
  nowMs: number;
  /** Coluna de ações (a `ActionsCell` da #1994), montada pelo container. */
  actions: ReactNode;
  /**
   * Conteúdo da linha aberta (o painel de cenários da #2002), chamado só com a linha aberta.
   * Entra sem moldura: a linha dá o separador, o fundo e o espaçamento. Sem esta prop o chevron
   * fica `aria-disabled`.
   */
  renderExpanded?: () => ReactNode;
  /** Destaque temporário (#2004): fundo e anel verdes, com transição de 1,2 s. */
  highlighted?: boolean;
}

/**
 * Linha da nova aba Meus Estudos (.dc.html:287-315), na grade de 5 colunas de `_grid.scss`:
 * descrição, horizonte, status · última execução, ações (slot) e chevron.
 *
 * A linha não é clicável: só o chevron abre e fecha, e o corpo é montado só com a linha aberta.
 * Cada linha guarda a própria expansão, então várias ficam abertas ao mesmo tempo (D10). Como
 * não há `Accordion.Header`, nenhum botão fica dentro de outro e uma ação não expande a linha.
 *
 * Contrato para quem monta a lista (#1999) e o painel (#2002):
 * - `key={study.id}`, nunca o índice: com o índice, o painel aberto de um estudo passa para outro
 *   quando a página muda ou a lista reordena;
 * - não trocar as linhas pelo skeleton em refetch ou polling: desmontar a linha perde a expansão
 *   (o `PendingOverlay` da #1992 esmaece a lista sem desmontá-la);
 * - as linhas são irmãs diretas, sem `<li>` nem outro invólucro e sem `gap` no container: os 8 px
 *   entre elas vêm de `.my-studies-row + .my-studies-row`;
 * - `renderExpanded` é chamado a cada render com a linha aberta: deve só devolver um componente
 *   (que busca os cenários), sem efeito colateral nem requisição;
 * - o painel tem `gap: 16px` entre filhos, que só atua se `renderExpanded` devolver vários
 *   (um fragmento); um filho único cuida do próprio espaçamento.
 */
export default function StudyRow({
  study, userId, nowMs, actions, renderExpanded, highlighted = false,
}: StudyRowProps) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const toggle = useCallback(() => setOpen((value) => !value), []);

  const { rowDetail, expandable } = useMyStudiesProduct();
  const detail = rowDetail(study);
  const canExpand = expandable && renderExpanded !== undefined;
  // Sem `renderExpanded` a linha fecha de vez: quando ele volta, ela não reabre sozinha. Ajuste
  // de estado durante o render (padrão do React para estado derivado de prop), sem efeito.
  if (open && !canExpand) setOpen(false);
  const isOpen = open && canExpand;
  const toggleTitle = isOpen ? COLLAPSE_TITLE : EXPAND_TITLE;
  // Nome vazio do backend conta como sem criador: o trecho some, nunca vira "Criado por ".
  const creatorName = study.creator?.name.trim();
  const origin = study.origin ?? null;
  const execution = formatExecutionLine(study, nowMs);

  return (
    <div className={`my-studies-row${highlighted ? ' my-studies-row--highlighted' : ''}`}>
      <div className="my-studies-grid my-studies-row-grid">
        <div className="my-studies-row-description">
          <div className="my-studies-row-name">{study.name}</div>
          <div className="my-studies-row-meta">
            <span className="my-studies-row-company">{study.company.name}</span>
            {creatorName && (
              <span>
                {'Criado por '}
                <b className="my-studies-row-creator">{creatorName}</b>
                {isCreatedByMe(study, userId) && ' (você)'}
              </span>
            )}
            {study.createdAt !== null && (
              <span>{`${creatorName ? 'em' : 'Criado em'} ${formatInstantDate(study.createdAt)}`}</span>
            )}
            {origin !== null && (
              <span className="my-studies-row-origin" title={ORIGIN_TITLE[origin]}>
                {ORIGIN_LABEL[origin]}
              </span>
            )}
            <span className="my-studies-row-id" title={ID_TITLE}>{`ID ${study.id}`}</span>
          </div>
        </div>

        <div className="my-studies-row-horizon">
          <span className="my-studies-row-horizon-range">{formatHorizon(study.horizon)}</span>
          {detail !== null && (
            <span className="my-studies-row-detail" title={detail}>{detail}</span>
          )}
        </div>

        <div className="my-studies-row-status">
          <StudyStatusPill status={study.status} fallbackLabel={study.statusLabel} />
          <span className="my-studies-row-execution" title={execution.title ?? undefined}>
            <span className="my-studies-row-execution-label">{execution.label}</span>
            {execution.when !== null && (
              <>
                {' '}
                <b>{execution.when}</b>
              </>
            )}
          </span>
          <span className="my-studies-row-detail">{formatResponseLine(study, nowMs)}</span>
          {study.internalError && (
            <span className="my-studies-row-interrupted">{INTERRUPTED_MESSAGE}</span>
          )}
        </div>

        <div className="my-studies-row-actions">{actions}</div>

        {expandable ? (
          <Button
            type="tertiary"
            icon="down_arrow"
            className="my-studies-row-toggle"
            title={toggleTitle}
          // Nome fixo e com contexto; aberto ou fechado é o `aria-expanded` que diz
            aria-label={`Cenários e blocos de ${study.name}`}
            aria-expanded={isOpen}
            aria-controls={canExpand ? panelId : undefined}
            aria-disabled={!canExpand || undefined}
            onClick={toggle}
          />
        ) : (
          <span className="my-studies-row-toggle my-studies-row-toggle--none" aria-hidden="true" />
        )}
      </div>

      {isOpen && renderExpanded && (
        <div id={panelId} className="my-studies-row-panel">
          {renderExpanded()}
        </div>
      )}
    </div>
  );
}
